import React, { useState } from "react";
import { Card, Button, Tag, Avatar } from "antd";
import {
    EyeOutlined,
    EyeInvisibleOutlined,
    CopyOutlined,
    MailOutlined,
    PhoneOutlined
} from "@ant-design/icons";
import { message } from "antd";
import { cn } from "../../../../utils/cn";
import { getInitials } from "../../../../utils/helper";
import { formatDateTime } from "../../../../utils/functions";

const EnhanceZapierCardView = ({
    record,
    actions = [],
    onCardClick,
}) => {
    const [passwordVisibility, setPasswordVisibility] = useState({});
    const [copyClipboardText, setCopyClipboardText] = useState("");

    const initials = getInitials(record?.userDetails?.name || "");

    const togglePasswordVisibility = (id) => {
        setPasswordVisibility((prev) => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleCopyEmail = (email) => {
        navigator.clipboard.writeText(email);
        setCopyClipboardText(email);
        message.success("Email copied to clipboard");
        setTimeout(() => setCopyClipboardText(""), 2000);
    };

    const handleCopyPassword = (password) => {
        navigator.clipboard.writeText(password);
        setCopyClipboardText(password);
        message.success("Password copied to clipboard");
        setTimeout(() => setCopyClipboardText(""), 2000);
    };

    const filteredActions = actions
        .map((action) => {
            if (action.condition && !action.condition(record)) {
                return null;
            }
            if (action.transform) {
                return action.transform(record, action);
            }
            return action;
        })
        .filter(Boolean);

    const getStatusBadge = (isCompleted) => {
        if (isCompleted) return <Tag color="success">Completed</Tag>;
        return <Tag color="warning">Pending</Tag>;
    };

    return (
        <Card
            hoverable
            className="h-full relative"
            onClick={() => onCardClick && onCardClick(record)}
        >
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 overflow-hidden">
                        <Avatar
                            size={48}
                            className="flex-shrink-0 font-semibold text-lg !bg-primary"
                        >
                            {initials}
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-base mb-1 truncate" title={record?.userDetails?.name}>
                                {record?.userDetails?.name || "N/A"}
                            </h3>
                            {record?.userDetails?.email && (
                                <p className="text-gray-500 text-sm mb-0 truncate" title={record?.userDetails?.email}>
                                    <MailOutlined className="mr-1" />
                                    {record?.userDetails?.email}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 border-b border-gray-200 pb-3">
                    {record?.userDetails?.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                            <PhoneOutlined className="mr-2 flex-shrink-0" />
                            <span className="truncate" title={record?.userDetails?.phone}>
                                {record?.userDetails?.phone}
                            </span>
                        </div>
                    )}
                </div>

                {/* Zapier Details Section */}
                <div className="space-y-4">
                    {/* Zapier Email */}
                    <div className="flex justify-between items-center w-full gap-2">
                        <span className="text-sm text-gray-600 font-medium">
                            Zapier Email:
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-sm truncate max-w-[150px]" title={record?.email}>
                                {record?.email}
                            </span>
                            {copyClipboardText === record?.email ? (
                                <Tag color="green">Copied</Tag>
                            ) : (
                                <CopyOutlined
                                    className="text-success cursor-pointer text-lg"
                                    title="Copy Email"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyEmail(record?.email);
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    {/* Zapier Password */}
                    <div className="flex justify-between items-center w-full gap-2">
                        <span className="text-sm text-gray-600 font-medium">
                            Zapier Password:
                        </span>
                        <div className="flex items-center gap-2">
                            <span
                                className="text-sm truncate max-w-[100px]"
                                style={!passwordVisibility[record._id] ? { filter: "blur(5px)" } : {}}
                            >
                                {record?.password}
                            </span>
                            {passwordVisibility[record._id] ? (
                                <EyeInvisibleOutlined
                                    className="text-success cursor-pointer text-lg"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePasswordVisibility(record._id);
                                    }}
                                    title="Hide Password"
                                />
                            ) : (
                                <EyeOutlined
                                    className="text-success cursor-pointer text-lg"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePasswordVisibility(record._id);
                                    }}
                                    title="Show Password"
                                />
                            )}
                            {copyClipboardText === record?.password ? (
                                <Tag color="green">Copied</Tag>
                            ) : (
                                <CopyOutlined
                                    className="text-success cursor-pointer text-lg"
                                    title="Copy Password"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyPassword(record?.password);
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    {/* Created At */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 font-medium">
                            Created At:
                        </span>
                        <span className="text-sm text-gray-700">
                            {formatDateTime(record?.createdAt)}
                        </span>
                    </div>

                    {/* Status */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 font-medium">
                            Status:
                        </span>
                        {getStatusBadge(record?.zapCreatedByAdmin)}
                    </div>
                </div>

                {/* Actions */}
                {filteredActions.length > 0 && (
                    <div
                        className={cn(
                            "grid grid-cols-2 gap-2 pt-3 border-t border-gray-200 mt-2",
                            filteredActions.length === 1 && "flex justify-end"
                        )}
                    >
                        {filteredActions.map((action, idx) => (
                            <Button
                                key={idx}
                                title={action.title ?? action.label}
                                className={cn(
                                    "text-sm font-medium h-9 my-1 !min-w-full flex justify-center items-center transition-colors duration-150 ease-in-out",
                                    action.className,
                                    filteredActions.length === 1 && "!w-1/2"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(record);
                                }}
                            >
                                {action.icon &&
                                    React.cloneElement(action.icon, { className: "mr-2" })}
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default EnhanceZapierCardView;